import React from 'react';
import { MdBolt, MdCheck, MdClose, MdContentCopy, MdDownload } from 'react-icons/md';
import { RiRobot3Line } from 'react-icons/ri';
import { baseurl } from '../../../Utils/Constants';

// The embed popup and block belong to this app, so they always use its teal theme, never the agent's own color.
const THEME_COLOR = 'var(--ai-teal-600, #3c9fa2)';

// The <script> tag a client pastes into their website. The script (public/aegis-assist.js) is served by
// this app and loads the agent's branding from the API on every page view, so later edits apply without
// re-pasting. A client can have several agents; data-agent-id picks which one this tag shows.
const snippetAttributes = (agentId) => [
    ['src', `${window.location.origin}/aegis-assist.js`],
    ['data-agent-id', agentId],
    ['data-api', baseurl],
];

// "--" would end the HTML comment early.
const commentLine = (title) => `<!-- ${(title || 'Aegis AI').replace(/-{2,}/g, '-')} agent -->`;

export const buildAssistEmbedSnippet = (agentId, title) => [
    commentLine(title),
    '<script',
    ...snippetAttributes(agentId).map(([name, value]) => `  ${name}="${value}"`),
    '  async',
    '></script>',
].join('\n');

// A ready-to-send file for the client's web team: the snippet plus where to paste it.
const buildEmbedFile = (snippet, title) => `<!--
  ${title || 'Aegis AI agent'} - website embed code

  HOW TO INSTALL
  1. Copy the <script> tag below.
  2. Paste it into your website's HTML, just before the closing </body> tag,
     on every page where the agent should appear (or once in a shared
     layout / footer template so it appears site-wide).
  3. Publish. The agent appears with its configured colors, icon and position.

  WordPress: use a "header and footer scripts" plugin and add it to the footer.
  Wix / Squarespace / Webflow: add it as custom code in the site footer.

  Colors, icon, title and position are loaded live from Aegis, so later changes
  apply automatically. There is no need to paste the code again.
-->
${snippet}
`;

const fileNameFor = (title) => `${(title || 'aegis-agent').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'aegis-agent'}-embed-code.html`;

const STEPS = [
    {
        title: 'Copy the script',
        text: <>Click <strong>Copy code</strong> below, or <strong>Download</strong> it as a file to send to your web team.</>,
    },
    {
        title: "Paste it into the website's HTML",
        text: <>Place it just before the closing <code>&lt;/body&gt;</code> tag on every page, or once in a shared footer / layout template.</>,
    },
    {
        title: 'Publish the website',
        text: 'The agent appears on the site. Later changes made here apply automatically, with no need to paste the code again.',
    },
];

// The same text as the copied snippet, colored like a code editor.
function HighlightedSnippet({ agentId, title }) {
    return (
        <code>
            <span className="hc-ai-tok-comment">{commentLine(title)}</span>{'\n'}
            <span className="hc-ai-tok-tag">&lt;script</span>{'\n'}
            {snippetAttributes(agentId).map(([name, value]) => (
                <React.Fragment key={name}>
                    {'  '}<span className="hc-ai-tok-attr">{name}</span>=<span className="hc-ai-tok-value">"{value}"</span>{'\n'}
                </React.Fragment>
            ))}
            {'  '}<span className="hc-ai-tok-attr">async</span>{'\n'}
            <span className="hc-ai-tok-tag">&gt;&lt;/script&gt;</span>
        </code>
    );
}

export function AssistEmbedCode({ agentId, title }) {
    const [copied, setCopied] = React.useState(false);
    const snippet = buildAssistEmbedSnippet(agentId, title);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            console.error(error);
        }
    };

    const download = () => {
        const blob = new Blob([buildEmbedFile(snippet, title)], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNameFor(title);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="hc-ai-embed" style={{ '--agent-color': THEME_COLOR }}>
            <ol className="hc-ai-embed-steps list-unstyled d-grid m-0">
                {STEPS.map((step, index) => (
                    <li key={step.title} className="hc-ai-embed-step d-flex align-items-start gap-2">
                        <span className="hc-ai-embed-step-num d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold">{index + 1}</span>
                        <span style={{ minWidth: 0 }}>
                            <span className="d-block fw-bold hc-ai-embed-step-title">{step.title}</span>
                            <span className="d-block hc-ai-embed-step-text">{step.text}</span>
                        </span>
                    </li>
                ))}
            </ol>

            <div className="hc-ai-embed-code overflow-hidden">
                <div className="hc-ai-embed-code-bar d-flex align-items-center justify-content-between gap-2">
                    <span className="d-inline-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span className="hc-ai-embed-dots d-inline-flex gap-1" aria-hidden="true"><i /><i /><i /></span>
                        <span className="hc-ai-embed-file text-truncate">index.html</span>
                    </span>
                    <span className="d-inline-flex gap-1 flex-shrink-0">
                        <button type="button" className="hc-ai-embed-btn d-inline-flex align-items-center gap-1 fw-bold" onClick={download} title="Download the embed code as a file">
                            <MdDownload size={15} />
                            <span className="d-none d-sm-inline">Download</span>
                        </button>
                        <button type="button" className={`hc-ai-embed-btn is-primary d-inline-flex align-items-center gap-1 fw-bold ${copied ? 'is-copied' : ''}`} onClick={copy}>
                            {copied ? <MdCheck size={15} /> : <MdContentCopy size={14} />}
                            {copied ? 'Copied!' : 'Copy code'}
                        </button>
                    </span>
                </div>
                <pre className="m-0"><HighlightedSnippet agentId={agentId} title={title} /></pre>
            </div>

        </div>
    );
}

export function AssistEmbedModal({ agentId, title, icon: Icon = RiRobot3Line, onClose }) {
    return (
        <div className="hc-ai-modal-backdrop d-flex align-items-center justify-content-center p-3" style={{ zIndex: 1060 }} onClick={onClose}>
            <div
                className="hc-ai-embed-modal bg-white d-flex flex-column"
                role="dialog"
                aria-label="Embed code"
                style={{ '--agent-color': THEME_COLOR }}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="hc-ai-embed-hero position-relative overflow-hidden text-white">
                    <span className="hc-ai-embed-hero-glow" aria-hidden="true" />
                    <button type="button" className="hc-ai-embed-close d-inline-flex align-items-center justify-content-center" onClick={onClose} aria-label="Close">
                        <MdClose size={18} />
                    </button>
                    <div className="d-flex align-items-center gap-3 pe-5">
                        <span className="hc-ai-embed-hero-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                            <Icon size={26} />
                            <span className="hc-ai-embed-hero-check d-inline-flex align-items-center justify-content-center"><MdCheck size={12} /></span>
                        </span>
                        <div style={{ minWidth: 0 }}>
                            <h5 className="fw-bold mb-1 text-white">{title ? `${title} is ready to go live` : 'Your agent is ready to go live'}</h5>
                            <p className="mb-0 hc-ai-embed-hero-sub">
                                <MdBolt size={15} /> Add it to your website's source code with a single script tag.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="hc-ai-embed-body">
                    <AssistEmbedCode agentId={agentId} title={title} />
                </div>
                <div className="hc-ai-embed-foot d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <small className="text-muted">You can find this code again on the agent's view page.</small>
                    <button type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary px-4" onClick={onClose}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
